import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from 'components/mui';
import { DeleteIcon, UploadIcon, DownloadIcon } from 'components/icons';
import UppyDashboard from 'components/Uppy/UppyDashboard';
import useCaseAttachments from 'containers/pages/cases/hooks/useCaseAttachments';
import DeleteAttachmentDialog from './DeleteAttachmentDialog';

interface CaseAttachmentsProps {
  caseId: number;
  customerId: number;
  secure?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function CaseAttachments({ caseId, customerId, secure }: CaseAttachmentsProps) {
  const { state, handler } = useCaseAttachments(caseId, secure);
  const title = secure ? 'Secure Attachments' : 'Attachments';

  const uploadMetaData = useMemo(() => ({
    CustomerId: customerId,
    CaseId: caseId,
    ...(secure ? { Secure: true } : {}),
  }), [customerId, caseId, secure]);

  if (state.loading && state.attachments.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Paper>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title} ({state.attachments.length})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadIcon />}
          onClick={handler.openUploadModal}
        >
          Upload
        </Button>
      </Box>

      <Paper elevation={1}>
        {state.attachments.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No attachments</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    Size
                  </TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.attachments.map((attachment) => (
                  <TableRow key={attachment.Id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component="a"
                        href={attachment.Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {attachment.FileName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {attachment.Description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {formatFileSize(attachment.FileSize)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {attachment.UploadedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {new Date(attachment.UploadedOn).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handler.openDeleteDialog(attachment)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <UppyDashboard
        metaData={uploadMetaData}
        onSingleUploadSuccess={handler.onUploadSuccess}
        open={state.uploadModalOpen}
        handleClose={handler.closeUploadModal}
      />

      <DeleteAttachmentDialog
        open={state.deleteTarget !== null}
        onClose={handler.closeDeleteDialog}
        onConfirm={handler.confirmDelete}
        fileName={state.deleteTarget?.FileName || ''}
      />
    </>
  );
}

export default CaseAttachments;
